import React, { useEffect, useState, useRef } from 'react';
import { Plus, AlertCircle, RefreshCw, Users, ChevronDown } from 'lucide-react';
import Layout from '../layout/Layout';
import Button from '../ui/Button';
import StoryCard from './StoryCard';
import CreateStoryModal from './CreateStoryModal';
import LoadingSpinner from '../ui/LoadingSpinner';
import PendingInvitationsBanner from '../family/PendingInvitationsBanner';
import IncompleteProfileBanner from './IncompleteProfileBanner';
import NotificationBackfillBanner from './NotificationBackfillBanner';
import PermissionRecoveryBanner from '../notifications/PermissionRecoveryBanner';
import ErrorBoundary from '../error/ErrorBoundary';
import OnboardingTooltip from '../onboarding/OnboardingTooltip';
import ActivePrompt from './ActivePrompt';
import { useAuthStore } from '../../store/authStore';
import { useStoryStore } from '../../store/storyStore';
import { useFamilyGroupStore } from '../../store/familyGroupStore';
import { useOnboardingStore } from '../../store/onboardingStore';
import { supabase } from '../../lib/supabase';
import { getUserFriendlyError } from '../../lib/queryUtils';
import { requestDeduplicator } from '../../lib/requestCache';

const Dashboard: React.FC = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [userFirstName, setUserFirstName] = useState<string | null>(null);
  const [userLastName, setUserLastName] = useState<string | null>(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [storiesError, setStoriesError] = useState<string | null>(null);
  const [introDismissed, setIntroDismissed] = useState(false);
  const createStoryRef = useRef<HTMLButtonElement>(null);
  const { user, userProfile, loadUserProfile } = useAuthStore();
  const { stories, loading } = useStoryStore();
  const { activeFamilyId, getActiveFamily, familyGroups, loading: familiesLoading } = useFamilyGroupStore();
  const { loaded: onboardingLoaded, loadProgress, shouldShowStep, dismissCurrentStep, completeStep, resetDismissals } = useOnboardingStore();

  const activeFamily = getActiveFamily();

  useEffect(() => {
    const justAcceptedInvitation = sessionStorage.getItem('just_accepted_invitation');
    const invitationFamilyId = sessionStorage.getItem('invitation_family_id');

    if (justAcceptedInvitation === 'true' && invitationFamilyId) {
      console.log('[Dashboard] Detected fresh invitation acceptance, preserving family:', invitationFamilyId);
      sessionStorage.removeItem('just_accepted_invitation');
      sessionStorage.removeItem('invitation_family_id');
      console.log('[Dashboard] Skipping fetchFamilyGroups to preserve invitation flow state');
    } else {
      console.log('[Dashboard] Normal load, fetching family groups');
      useFamilyGroupStore.getState().fetchFamilyGroups();
    }

    loadUserProfileData();

    if (user?.id) {
      loadProgress(user.id);
    }
    resetDismissals();
  }, []);

  const loadUserProfileData = async () => {
    if (!user) {
      setProfileLoaded(true);
      return;
    }

    if (userProfile?.firstName) {
      setUserFirstName(userProfile.firstName);
      setUserLastName(userProfile.lastName);
      setProfileLoaded(true);
      return;
    }

    await loadUserProfile();

    const currentProfile = useAuthStore.getState().userProfile;
    if (currentProfile?.firstName) {
      setUserFirstName(currentProfile.firstName);
      setUserLastName(currentProfile.lastName);
    } else {
      const { data } = await supabase
        .from('user_profiles')
        .select('first_name, last_name')
        .eq('id', user.id)
        .maybeSingle();

      if (data) {
        setUserFirstName(data.first_name);
        setUserLastName(data.last_name);
      }
    }

    setProfileLoaded(true);
  };

  useEffect(() => {
    if (activeFamilyId) {
      console.log('[Dashboard] Active family changed to:', activeFamilyId);

      const { fetchStoriesForFamily, reset: resetStoryStore } = useStoryStore.getState();

      const invitationFamilyId = sessionStorage.getItem('invitation_family_id');
      const isInvitationFlow = invitationFamilyId === activeFamilyId;

      if (isInvitationFlow) {
        console.log('[Dashboard] This is the invitation family, giving extra time for propagation');
        const timeoutId = setTimeout(() => {
          resetStoryStore();
          setStoriesError(null);
          requestDeduplicator.invalidateCachePattern('fetchStoriesForFamily');

          fetchStoriesForFamily(activeFamilyId)
            .catch((error) => {
              console.error('[Dashboard] Error fetching stories:', error);
              setStoriesError(getUserFriendlyError(error));
            });
        }, 500);

        return () => {
          clearTimeout(timeoutId);
        };
      } else {
        resetStoryStore();
        setStoriesError(null);
        requestDeduplicator.invalidateCachePattern('fetchStoriesForFamily');

        fetchStoriesForFamily(activeFamilyId)
          .catch((error) => {
            console.error('[Dashboard] Error fetching stories:', error);
            setStoriesError(getUserFriendlyError(error));
          });
      }
    }

    return () => {
      console.log('[Dashboard] Cleaning up on unmount or family change');
    };
  }, [activeFamilyId]);

  const openFamilySelector = () => {
    window.dispatchEvent(new CustomEvent('openFamilySelector'));
  };

  const openCreateFamily = () => {
    window.dispatchEvent(new CustomEvent('openCreateFamily'));
  };

  const retryFetchStories = () => {
    if (activeFamilyId) {
      console.log('[Dashboard] Retrying story fetch for family:', activeFamilyId);
      setStoriesError(null);

      const { fetchStoriesForFamily, reset: resetStoryStore } = useStoryStore.getState();
      resetStoryStore();

      setTimeout(() => {
        fetchStoriesForFamily(activeFamilyId)
          .catch((error) => {
            console.error('[Dashboard] Error fetching stories:', error);
            setStoriesError(getUserFriendlyError(error));
          });
      }, 100);
    }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="mb-4 sm:mb-6">
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-stone-900 mb-1.5">
            {userFirstName ? `Hola, ${userFirstName}` : 'Tus Historias'}
          </h1>
          {/* PRO-344: the active family is a FILTER, not a label. The chip makes
              it explicit and clickable — the single family-switching surface on
              the dashboard (the AppHeader pill was removed as redundant).
              Zero-families state (reachable via delete/leave family): the pill
              becomes a create-family CTA. Nothing renders while families load. */}
          {activeFamily ? (
            <button
              onClick={openFamilySelector}
              aria-label="Cambiar familia"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-stone-100 border border-stone-200 text-[12.5px] font-medium text-stone-700 hover:bg-stone-200 transition-colors !min-h-0 !min-w-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500 focus-visible:ring-offset-1"
            >
              <Users className="h-[13px] w-[13px] text-rose-500" />
              <span>Estás viendo: {activeFamily.name}</span>
              <ChevronDown className="h-3.5 w-3.5 text-rose-500" />
            </button>
          ) : (!familiesLoading && familyGroups.length === 0 && (
            <button
              onClick={openCreateFamily}
              aria-label="Crear familia"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-rose-50 border border-rose-200 text-[12.5px] font-medium text-rose-700 hover:bg-rose-100 transition-colors !min-h-0 !min-w-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500 focus-visible:ring-offset-1"
            >
              <Plus className="h-[13px] w-[13px] text-rose-500" />
              <span>Crea tu primera familia</span>
            </button>
          ))}
        </div>

        {profileLoaded && (
          <IncompleteProfileBanner
            firstName={userFirstName}
            lastName={userLastName}
          />
        )}

        <PendingInvitationsBanner />
        <div className="space-y-3 mb-4 empty:hidden">
          <PermissionRecoveryBanner />
          <NotificationBackfillBanner />
        </div>
        <ActivePrompt />

        <ErrorBoundary level="component" componentName="StoriesGrid">
          {loading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner message="Cargando tus historias..." />
            </div>
          ) : storiesError ? (
            <div className="flex justify-center py-12">
              <div className="bg-rose-50 border border-rose-200 rounded-xl p-8 max-w-md">
                <div className="flex items-start space-x-4 mb-6">
                  <AlertCircle className="h-8 w-8 text-rose-600 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-rose-900 mb-2">Error al cargar historias</h3>
                    <p className="text-sm text-rose-800">{storiesError}</p>
                  </div>
                </div>
                <Button
                  onClick={retryFetchStories}
                  icon={RefreshCw}
                  className="w-full bg-rose-600 hover:bg-rose-700 text-white"
                >
                  Reintentar
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
              <button
                ref={createStoryRef}
                onClick={() => {
                  setIsCreateModalOpen(true);
                }}
                className="group relative bg-white border-2 border-dashed border-stone-200 rounded-lg sm:rounded-xl p-4 sm:p-5 hover:border-rose-400 hover:shadow-md transition-all duration-200 flex flex-col items-center justify-center touch-manipulation min-h-[100px] sm:min-h-[230px] lg:min-h-[260px]"
              >
                <div className="relative mb-3">
                  <div className="absolute inset-0 bg-rose-200 rounded-full animate-ping opacity-30" style={{ animationDuration: '2.5s' }}></div>
                  <div className="relative bg-rose-100 rounded-full p-3 group-hover:bg-rose-200 transition-colors">
                    <Plus className="h-6 w-6 text-rose-600" />
                  </div>
                </div>
                <h3 className="text-base font-semibold text-stone-900 mb-1">
                  Empezar nueva historia
                </h3>
                <p className="text-xs text-stone-500 text-center">
                  Preserva los recuerdos de alguien especial
                </p>
              </button>

              {stories.map((story) => (
                <ErrorBoundary key={story.id} level="component" componentName="StoryCard">
                  <StoryCard story={story} />
                </ErrorBoundary>
              ))}
            </div>
          )}
        </ErrorBoundary>

      </div>

      <OnboardingTooltip
        centerOnScreen
        title="Tu panel principal"
        description="Aquí verás todas las historias creadas dentro de tu grupo familiar y podrás crear nuevas. Para cambiar de familia o crear otra, toca el botón 'Estás viendo' justo debajo del saludo."
        stepLabel="Primeros pasos"
        show={onboardingLoaded && shouldShowStep(1) && !introDismissed && !loading && !storiesError && !isCreateModalOpen}
        onDismiss={() => setIntroDismissed(true)}
        actionLabel="Entendido"
        onAction={() => setIntroDismissed(true)}
      />

      <OnboardingTooltip
        targetRef={createStoryRef as React.RefObject<HTMLElement>}
        title="¿A quién quieres recordar?"
        description="Pulsa aquí para crear tu primera historia. Te pediremos el nombre de la persona y una breve descripción para personalizar los temas."
        position="bottom"
        stepLabel="Paso 1"
        show={onboardingLoaded && shouldShowStep(1) && introDismissed && !loading && !storiesError && !isCreateModalOpen}
        onDismiss={dismissCurrentStep}
        allowClickThrough={true}
      />

      <CreateStoryModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      />
    </Layout>
  );
};

export default Dashboard;
